import { LoadingButton } from '@mui/lab';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Notify } from 'notiflix';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { addIncomeCollection } from '../../../data/api/incomes/IncomeRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';
import styled from 'styled-components';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { getCombos } from '../../../data/api/combos/combos';

const nowDate = new Date(Date.now());
const minDate = `${nowDate.getFullYear()}-${nowDate.getMonth() + 1 > 9 ? nowDate.getMonth() + 1 : '0' + (nowDate.getMonth() + 1)}-${nowDate.getDate()}`;

const prefijoDesc = 'El prefijo es un texto o siglas cortas que se colocaran al inicio del código para identificar fácilmente quién creo el código, NO es obligatorio.';
const expiresAtDesc = 'Habilitar una fecha de expiración de los códigos, si no se selecciona su fecha de expiración será en el año 2099.';
const publicCodesDesc = 'Los códigos públicos son aquellos que podrán ser usados en los juegos que se encuentran fuera de la página de usuarios registrados, recuerda que esta opción depende también de la opción "Usuarios registrados".';
const privateCodesDesc = 'Los códigos privados son aquellos que podrán ser usados solo en los juegos que se encuentran dentro de la página de usuarios registrados, recuerda que esta opción solo es válida para usuarios registrados.';
const userRegisterDesc = 'Los códigos solo serán usados por usuarios que estén registrados en el sistema, esto facilitara el análisis de las jugadas realizadas.';
const movesByCodeDesc = 'Cantidad de jugadas por codigo creado, si es mayor a 1 solo se podran usar para usarios que esten registrados.';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        background: '#fff',
        color: '#10285f',
        fontWeight: 700,
        transition: '0.25s ease',

        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.16)',
        },

        '&:hover fieldset': {
            borderColor: 'rgba(214, 170, 58, 0.7)',
        },

        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
            borderWidth: '2px',
        },
    },

    '& .MuiInputLabel-root': {
        color: 'rgba(15, 34, 77, 0.65)',
        fontWeight: 700,
    },

    '& .MuiInputLabel-root.Mui-focused': {
        color: '#10285f',
    },
};

const helperStyles = {
    color: 'rgba(15, 34, 77, 0.62)',
    fontWeight: 700,
    marginBottom: '6px',
};

/**
 * Bloquea negativos / notación científica y evita que la rueda del mouse
 * cambie el valor numérico cuando el input tiene foco. Por defecto los
 * <input type="number"> en navegadores modernos sí lo cambian — basta
 * pasar por encima haciendo scroll para alterar la cantidad por error.
 */
const numberFieldProps = (extra = {}) => ({
    type: 'number',
    onWheel: (e) => e.target.blur(),
    onKeyDown: (e) => {
        if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
    },
    inputProps: { min: 1, step: 1, ...extra },
});

const GameCodesForm = ({ loadData = () => { }, onClose = () => { } }) => {
    const { auth, token, validateUser } = useIndex();
    const { register, handleSubmit, reset, setValue } = useForm();

    const [submit, setSubmit] = useState(false);

    const [expanded, setExpanded] = useState('combos');
    const handleAccordion = (panel) => (e, isExpanded) => setExpanded(isExpanded ? panel : false);

    const [combos, setCombos] = useState([]);
    const [loadingCombos, setLoadingCombos] = useState(true);

    const [selectedComboId, setSelectedComboId] = useState('');
    const [comboQty, setComboQty] = useState(1);

    const [expiresDate, setExpiresDate] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [prefixCode, setPrefixCode] = useState('');
    const [movesByCode, setMovesByCode] = useState(0);

    const loadCombos = async () => {
        setLoadingCombos(true);

        try {
            const { data } = await getCombos(token, { status: 1 });

            if (data?.success) {
                const list = data?.data ?? [];
                setCombos(list);
                setSelectedComboId((prev) => prev || (list?.[0]?.id ?? ''));
            } else {
                setCombos([]);
                setSelectedComboId('');
            }
        } catch (error) {
            validErrors(error);
            setCombos([]);
            setSelectedComboId('');
        } finally {
            setLoadingCombos(false);
        }
    };

    useEffect(() => {
        loadCombos();
    }, []);

    const selectedCombo = useMemo(
        () => combos.find(c => String(c.id) === String(selectedComboId)) ?? null,
        [combos, selectedComboId]
    );

    const onSubmit = async (dataForm) => {
        setSubmit(true);

        if (expanded === 'combos') {
            const qty = Math.max(1, parseInt(comboQty || 1));
            const name = selectedCombo?.name?.trim();

            dataForm = {
                ...dataForm,
                name,
                code_count: 1,
                code_amount: selectedCombo?.moves ?? 0,
                combo_amount: qty,
                combo_id: selectedCombo?.id,
                start_at: minDate,
                expires_at: null,
                prefix: '',
                public_code: true,
                private_code: true,
                user_register: true,
            };

            setValue('name', dataForm.name);
            setValue('code_count', dataForm.code_count);
            setValue('code_amount', dataForm.code_amount);
            setValue('start_at', dataForm.start_at);
            setValue('expires_at', dataForm.expires_at);
            setValue('prefix', dataForm.prefix);
            setValue('public_code', dataForm.public_code);
            setValue('private_code', dataForm.private_code);
            setValue('user_register', dataForm.user_register);
        }

        if (dataForm['code_amount'] > 1) {
            dataForm['public_code'] = false;
        }

        try {
            const { data } = await addIncomeCollection(token, dataForm);

            if (data.success) {
                reset();
                setPrefixCode('');
                setMovesByCode(0);
                setExpiresDate(false);
                setStartDate(null);
                setSelectedComboId(combos?.[0]?.id ?? '');
                setComboQty(1);

                loadData();
                onClose();
            } else {
                Notify.failure(data.message);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmit(false);
            validateUser?.();
        }
    };

    return (
        <FormWrapper onSubmit={handleSubmit(onSubmit)}>
            {auth?.role_id !== 1 && (
                <MovesNotice>
                    <span>Tienes</span>
                    <strong>{auth?.moves}</strong>
                    <span>jugadas para crear</span>
                </MovesNotice>
            )}

            <StyledAccordion
                expanded={expanded === 'combos'}
                onChange={handleAccordion('combos')}
                disableGutters
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <AccordionTitle>
                        <Box>
                            <Typography className="title">Combos</Typography>
                        </Box>

                        <Chip
                            size="small"
                            label="Rápido"
                            sx={{
                                fontWeight: 900,
                                color: '#10285f',
                                background: 'rgba(214, 170, 58, 0.20)',
                                border: '1px solid rgba(214, 170, 58, 0.35)',
                            }}
                        />
                    </AccordionTitle>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DescriptionBox>
                                Selecciona un combo y cuántas veces lo deseas. Se crearán los códigos automáticamente.
                            </DescriptionBox>
                        </Grid>

                        <Grid item xs={12}>
                            {loadingCombos ? (
                                <LoadingBox>
                                    <CircularProgress size={28} sx={{ color: '#d6aa3a' }} />
                                    <Typography>Cargando combos...</Typography>
                                </LoadingBox>
                            ) : combos.length === 0 ? (
                                <EmptyBox>
                                    No hay combos activos.
                                </EmptyBox>
                            ) : (
                                <ComboButtonsWrap>
                                    {combos.map((c) => {
                                        const selected = String(c.id) === String(selectedComboId);

                                        return (
                                            <ComboBtn
                                                key={c.id}
                                                type="button"
                                                $active={selected}
                                                onClick={() => setSelectedComboId(c.id)}
                                                disabled={submit}
                                                aria-pressed={selected}
                                            >
                                                <ComboPill $active={selected}>
                                                    <LocalOfferOutlinedIcon style={{ fontSize: 16 }} />
                                                    ${String(c.value ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                                                </ComboPill>

                                                <span className="separator">×</span>

                                                <ComboPill $active={selected}>
                                                    <ConfirmationNumberOutlinedIcon style={{ fontSize: 16 }} />
                                                    {c.moves ?? 0} JUGADAS
                                                </ComboPill>
                                            </ComboBtn>
                                        );
                                    })}
                                </ComboButtonsWrap>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="stretch">
                                <Grid item xs={12} md={4}>
                                    <FormHelperText sx={helperStyles}>
                                        Cantidad de combos
                                    </FormHelperText>

                                    <TextField
                                        size="small"
                                        fullWidth
                                        disabled={submit || loadingCombos || combos.length === 0}
                                        value={comboQty}
                                        onChange={(e) => setComboQty(e.target.value)}
                                        {...numberFieldProps({ min: 1 })}
                                        sx={fieldStyles}
                                    />

                                    <Typography className="field-tip">
                                        Tip: presiona Enter para crear más rápido.
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={8}>
                                    <SummaryCard>
                                        <SummaryRow>
                                            <Typography className="summary-title">
                                                Resumen
                                            </Typography>

                                            <SummaryItem>
                                                <span>Combo:</span>
                                                <b>{selectedCombo?.name ?? '-'}</b>
                                            </SummaryItem>

                                            <SummaryItem>
                                                <span>Repeticiones:</span>
                                                <b>{Math.max(1, parseInt(comboQty || 1))}</b>
                                            </SummaryItem>
                                        </SummaryRow>

                                        <BigTotal>
                                            <span>Total Jugadas</span>
                                            <strong>
                                                {(selectedCombo?.moves ?? 0) * Math.max(1, parseInt(comboQty || 1))}
                                            </strong>
                                        </BigTotal>
                                    </SummaryCard>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <LoadingButton
                                fullWidth
                                loading={submit}
                                loadingPosition="start"
                                startIcon={<i className="fa-solid fa-floppy-disk"></i>}
                                variant="contained"
                                size="small"
                                type="submit"
                                disabled={loadingCombos || combos.length === 0 || !selectedComboId}
                                sx={{
                                    borderRadius: '14px',
                                    py: 1.2,
                                    fontWeight: 900,
                                    letterSpacing: 0.3,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                Crear desde combo
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </StyledAccordion>

            <StyledAccordion
                expanded={expanded === 'custom'}
                onChange={handleAccordion('custom')}
                disableGutters
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <AccordionTitle>
                        <Box>
                            <Typography className="title">Personalizado</Typography>
                            <Typography className="subtitle">
                                Configura manualmente los códigos de la colección
                            </Typography>
                        </Box>

                        <Chip
                            size="small"
                            label="Avanzado"
                            variant="outlined"
                            sx={{
                                fontWeight: 900,
                                color: '#10285f',
                                borderColor: 'rgba(15, 34, 77, 0.22)',
                                background: '#fff',
                            }}
                        />
                    </AccordionTitle>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormHelperText sx={helperStyles}>
                                Nombre de colección
                            </FormHelperText>

                            <TextField
                                placeholder="Nombre de colección"
                                size="small"
                                fullWidth
                                {...register('name')}
                                disabled={submit}
                                inputProps={{ maxLength: 50 }}
                                sx={fieldStyles}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormHelperText sx={helperStyles}>
                                Cantidad de códigos
                            </FormHelperText>

                            <TextField
                                placeholder="Cantidad de códigos min. 1"
                                size="small"
                                fullWidth
                                {...register('code_count')}
                                {...numberFieldProps({ min: 1 })}
                                disabled={submit}
                                sx={fieldStyles}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormHelperText sx={helperStyles}>
                                Jugadas por código{' '}
                                <Tooltip title={movesByCodeDesc} enterDelay={500} arrow>
                                    <i className="fa-regular fa-circle-question"></i>
                                </Tooltip>
                            </FormHelperText>

                            <TextField
                                placeholder="Jugadas por código min. 1"
                                size="small"
                                fullWidth
                                {...register('code_amount')}
                                {...numberFieldProps({ min: 1 })}
                                disabled={submit}
                                onChange={(e) => setMovesByCode(parseInt(e.target.value))}
                                sx={fieldStyles}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <StyledDivider>
                            </StyledDivider>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <OptionCard>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormHelperText sx={helperStyles}>
                                            Fecha de inicio
                                        </FormHelperText>

                                        <TextField
                                            size="small"
                                            type="date"
                                            fullWidth
                                            InputProps={{ inputProps: { min: minDate } }}
                                            {...register('start_at')}
                                            disabled={submit}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            sx={fieldStyles}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <OptionSwitchRow>
                                            <Typography>
                                                Expiración de códigos{' '}
                                                <Tooltip title={expiresAtDesc} enterDelay={500} arrow>
                                                    <i className="fa-regular fa-circle-question"></i>
                                                </Tooltip>
                                            </Typography>

                                            <Switch
                                                checked={expiresDate}
                                                onChange={() => setExpiresDate(!expiresDate)}
                                                disabled={submit}
                                            />
                                        </OptionSwitchRow>

                                        <FormHelperText sx={helperStyles}>
                                            Fecha de fin
                                        </FormHelperText>

                                        <TextField
                                            disabled={!expiresDate}
                                            size="small"
                                            type="date"
                                            fullWidth
                                            InputProps={{ inputProps: { min: startDate ?? minDate } }}
                                            {...register('expires_at')}
                                            sx={fieldStyles}
                                        />
                                    </Grid>
                                </Grid>
                            </OptionCard>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <OptionCard>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormHelperText sx={helperStyles}>
                                            Prefijo de código
                                        </FormHelperText>

                                        <TextField
                                            placeholder="Ejm: NAME"
                                            size="small"
                                            fullWidth
                                            {...register('prefix')}
                                            disabled={submit}
                                            value={prefixCode}
                                            onChange={(e) => setPrefixCode(e.target.value.toUpperCase())}
                                            inputProps={{ maxLength: 10 }}
                                            sx={fieldStyles}
                                        />

                                        <Typography className="field-tip">
                                            Ejemplo: {prefixCode && prefixCode.toUpperCase() + '_'}1A2B3C{' '}
                                            <Tooltip title={prefijoDesc} enterDelay={600} arrow>
                                                <i className="fa-regular fa-circle-question"></i>
                                            </Tooltip>
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <FormGroup>
                                            <StyledControlLabel
                                                control={
                                                    <Checkbox
                                                        defaultChecked
                                                        disabled={movesByCode > 1 || submit}
                                                        {...register('public_code')}
                                                    />
                                                }
                                                label={
                                                    <Typography>
                                                        {movesByCode > 1 ? (
                                                            <del>Sin registrar</del>
                                                        ) : (
                                                            'Sin registrar'
                                                        )}{' '}
                                                        <Tooltip title={publicCodesDesc} enterDelay={500} arrow>
                                                            <i className="fa-regular fa-circle-question"></i>
                                                        </Tooltip>
                                                    </Typography>
                                                }
                                            />

                                            <StyledControlLabel
                                                control={
                                                    <Checkbox
                                                        defaultChecked
                                                        {...register('private_code')}
                                                        disabled={submit}
                                                    />
                                                }
                                                label={
                                                    <Typography>
                                                        Registrados{' '}
                                                        <Tooltip title={privateCodesDesc} enterDelay={500} arrow>
                                                            <i className="fa-regular fa-circle-question"></i>
                                                        </Tooltip>
                                                    </Typography>
                                                }
                                            />
                                        </FormGroup>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <FormGroup>
                                            <StyledControlLabel
                                                control={
                                                    <Switch
                                                        disabled
                                                        defaultChecked={false}
                                                        {...register('user_register')}
                                                    />
                                                }
                                                label={
                                                    <Typography>
                                                        Usuarios registrados{' '}
                                                        <Tooltip title={userRegisterDesc} enterDelay={500} arrow>
                                                            <i className="fa-regular fa-circle-question"></i>
                                                        </Tooltip>
                                                    </Typography>
                                                }
                                            />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </OptionCard>
                        </Grid>

                        <Grid item xs={12}>
                            <LoadingButton
                                fullWidth
                                loading={submit}
                                loadingPosition="start"
                                startIcon={<i className="fa-solid fa-floppy-disk"></i>}
                                variant="contained"
                                size="small"
                                type="submit"
                                sx={{
                                    borderRadius: '14px',
                                    py: 1.2,
                                    fontWeight: 900,
                                    letterSpacing: 0.3,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                Guardar personalizado
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </StyledAccordion>
        </FormWrapper>
    );
};

export default GameCodesForm;

const FormWrapper = styled.form`
    width: 100%;

    .field-tip {
        display: block;
        margin-top: 0.45rem;
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.78rem;
        font-weight: 600;
    }

    .fa-circle-question {
        color: #d6aa3a;
        cursor: help;
    }
`;

const MovesNotice = styled.div`
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    border-radius: 16px;
    background: rgba(214, 170, 58, 0.14);
    border: 1px solid rgba(214, 170, 58, 0.28);
    color: #10285f;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    font-weight: 700;

    strong {
        font-size: 1.1rem;
        font-weight: 900;
        color: #8a6500;
    }
`;

const StyledAccordion = styled(Accordion)`
    margin-bottom: 0.9rem !important;
    border-radius: 18px !important;
    overflow: hidden;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%) !important;
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.06) !important;

    &::before {
        display: none;
    }

    .MuiAccordionSummary-root {
        min-height: 66px;
        border-bottom: 1px solid rgba(15, 34, 77, 0.08);
    }

    .MuiAccordionSummary-content {
        margin: 0.7rem 0;
    }

    .MuiAccordionDetails-root {
        padding: 1rem 1.2rem 1.2rem;
    }

    .MuiSvgIcon-root {
        color: #10285f;
    }
`;

const AccordionTitle = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    .title {
        margin: 0;
        color: #10285f;
        font-size: 1.05rem;
        font-weight: 900;
    }

    .subtitle {
        margin-top: 0.15rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.84rem;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.4rem;
    }
`;

const DescriptionBox = styled.div`
    padding: 0.75rem 1rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    color: rgba(15, 34, 77, 0.68);
    font-size: 0.9rem;
    font-weight: 600;
`;

const LoadingBox = styled.div`
    min-height: 110px;
    display: flex;
    gap: 0.7rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #10285f;
    font-weight: 700;
`;

const EmptyBox = styled.div`
    padding: 1rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.045);
    color: rgba(15, 34, 77, 0.62);
    font-weight: 700;
    text-align: center;
`;

const ComboButtonsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const ComboBtn = styled.button`
    border: 1.5px solid ${({ $active }) => ($active ? '#d6aa3a' : 'rgba(15, 34, 77, 0.12)')};
    background: ${({ $active }) =>
        $active
            ? 'linear-gradient(135deg, #10285f 0%, #1a2f5c 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f7f9ff 100%)'};
    color: ${({ $active }) => ($active ? '#fff' : '#10285f')};
    padding: 10px 14px;
    border-radius: 999px;
    font-weight: 900;
    font-size: 12.5px;
    letter-spacing: 0.2px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    box-shadow: ${({ $active }) =>
        $active
            ? '0 10px 24px rgba(16, 40, 95, 0.25)'
            : '0 6px 16px rgba(15, 34, 77, 0.08)'};
    transition: 0.18s ease;

    .separator {
        opacity: 0.7;
    }

    &:hover {
        transform: translateY(-1px);
        border-color: #d6aa3a;
        box-shadow: ${({ $active }) =>
        $active
            ? '0 14px 28px rgba(16, 40, 95, 0.30)'
            : '0 10px 22px rgba(15, 34, 77, 0.12)'};
    }

    &:active {
        transform: translateY(0px) scale(0.99);
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
        transform: none;
    }

    @media (max-width: 520px) {
        width: 100%;
        justify-content: center;
    }
`;

const ComboPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${({ $active }) =>
        $active ? 'rgba(255,255,255,.15)' : 'rgba(214, 170, 58, 0.14)'};
    color: inherit;
    font-weight: 900;
`;

const SummaryCard = styled.div`
    height: 100%;
    border: 1px solid rgba(15, 34, 77, 0.10);
    border-radius: 16px;
    padding: 14px;
    background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.05);
`;

const SummaryRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px 18px;
    align-items: center;
    justify-content: space-between;

    .summary-title {
        color: #10285f;
        font-weight: 900;
    }
`;

const SummaryItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(15, 34, 77, 0.65);
    font-size: 13px;
    font-weight: 700;

    b {
        color: #10285f;
        font-size: 13.2px;
        font-weight: 900;
    }
`;

const BigTotal = styled.div`
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 14px;
    background: rgba(214, 170, 58, 0.16);
    border: 1px solid rgba(214, 170, 58, 0.28);
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #10285f;
    font-weight: 800;

    strong {
        font-size: 1.2rem;
        font-weight: 900;
        color: #8a6500;
    }
`;

const StyledDivider = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    color: #10285f;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: rgba(15, 34, 77, 0.13);
    }

    span {
        padding: 0.35rem 0.9rem;
        border-radius: 999px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
    }
`;

const OptionCard = styled.div`
    height: 100%;
    padding: 1rem;
    border-radius: 18px;
    background: rgba(16, 40, 95, 0.035);
    border: 1px solid rgba(15, 34, 77, 0.08);
`;

const OptionSwitchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.5rem;

    p {
        color: #10285f;
        font-weight: 800;
        font-size: 0.9rem;
    }
`;

const StyledControlLabel = styled(FormControlLabel)`
    margin-left: 0 !important;
    margin-right: 0 !important;

    .MuiTypography-root {
        color: #10285f;
        font-size: 0.88rem;
        font-weight: 700;
    }

    .MuiCheckbox-root.Mui-checked,
    .MuiSwitch-switchBase.Mui-checked {
        color: #d6aa3a;
    }
`;